import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getToken } from "../utils/auth";
import swal from "sweetalert";
import "./MCQLibrary.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function MCQListByCategory() {
  const { category } = useParams();

  const [mcqs, setMcqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ---------- ASSIGN MODAL ---------- */
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedMcq, setSelectedMcq] = useState(null);

  const [campuses, setCampuses] = useState([]);
  const [years, setYears] = useState([]);
  const [batches, setBatches] = useState([]);

  const [campus, setCampus] = useState("");
  const [year, setYear] = useState("");
  const [batchesSelected, setBatchesSelected] = useState([]);

  const [students, setStudents] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignMessage, setAssignMessage] = useState("");

  /* ---------- VIEW MODAL ---------- */
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewMcq, setViewMcq] = useState(null);

  /* ---------- ASSIGNMENT MODAL ---------- */
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState("");

  console.log(assignLoading);
  

  /* ---------- FETCH MCQS ---------- */
  useEffect(() => {
    async function fetchMCQs() {
      try {
        const res = await fetch(`${API_BASE}/getAllMCQForAdmin`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });

        const data = await res.json();
        setMcqs(data.filter(mcq => mcq.category === category));
      } catch {
        setError("Unable to load MCQs");
      } finally {
        setLoading(false);
      }
    }

    fetchMCQs();
  }, [category]);

  /* ---------- FETCH CAMPUSES ---------- */
  useEffect(() => {
    fetch(`${API_BASE}/campus/get`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(res => res.json())
      .then(setCampuses)
      .catch(() => setCampuses([]));
  }, []);

  /* ---------- FETCH YEARS ---------- */
  useEffect(() => {
    if (!campus) return;

    fetch(`${API_BASE}/year/get-by-campus?campus=${encodeURIComponent(campus)}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(res => res.json())
      .then(data => {
        setYears(data);
        setYear("");
        setBatches([]);
        setBatchesSelected([]);
      });
  }, [campus]);

  /* ---------- FETCH BATCHES ---------- */
  useEffect(() => {
    if (!campus || !year) return;

    fetch(
      `${API_BASE}/batch/get-by-campus-year?campus=${encodeURIComponent(
        campus
      )}&year=${year}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    )
      .then(res => res.json())
      .then(data => {
        setBatches(data);
        setBatchesSelected([]);
      });
  }, [campus, year]);

  /* ---------- FETCH STUDENTS ---------- */
  useEffect(() => {
    if (!campus || !year || batchesSelected.length === 0) {
      setStudents([]);
      return;
    }

    async function fetchStudents() {
      const allStudents = [];
      const emailSet = new Set();

      for (const batch of batchesSelected) {
        try {
          const res = await fetch(
            `${API_BASE}/api/students/students?college=${encodeURIComponent(
              campus
            )}&year=${year}&batch=${batch}`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );

          const data = await res.json();

          data.forEach(s => {
            if (!emailSet.has(s.email)) {
              emailSet.add(s.email);
              allStudents.push(s);
            }
          });
        } catch {}
      }

      setStudents(allStudents);
    }

    fetchStudents();
  }, [campus, year, batchesSelected]);

  /* ---------- ASSIGN MCQ ---------- */
  const handleAssignMcq = async () => {
    if (!selectedMcq || students.length === 0) return;

    setAssignLoading(true);
    setAssignMessage(`Assigning to ${students.length} students...`);

    let success = 0;
    let failed = 0;

    for (const student of students) {
      try {
        await fetch(`${API_BASE}/addMcq`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({
            email: student.email,
            mcq: selectedMcq._id
          })
        });
        success++;
      } catch {
        failed++;
      }
    }

    setAssignMessage(`✅ Assigned: ${success}, ❌ Failed: ${failed}`);
    setAssignLoading(false);

    setTimeout(() => {
      setShowAssignModal(false);
      setCampus("");
      setYear("");
      setBatchesSelected([]);
      setStudents([]);
      setAssignMessage("");
    }, 2000);
  };

  /* ---------- FETCH ASSIGNMENTS ---------- */
  const fetchAssignments = async (mcq) => {
    setSelectedMcq(mcq);
    setShowAssignmentModal(true);
    setAssignmentLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/assignments`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      setAssignments(data);
    } catch {
      setAssignments([]);
    } finally {
      setAssignmentLoading(false);
    }
  };

  /* ---------- ATTACH ASSIGNMENT ---------- */
  const handleAttachAssignment = async () => {
  if (!selectedAssignment || !selectedMcq) {
    swal("Warning", "Please select an assignment", "warning");
    return;
  }

  try {
    const res = await fetch(
      `${API_BASE}/updateAssignmentStatus/${selectedMcq._id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          isAssignmentPresent: true,
          assignmentId: selectedAssignment
        })
      }
    );

    if (!res.ok) throw new Error();

    await swal({
      title: "Success!",
      text: "MCQ attached to assignment successfully",
      icon: "success",
      button: "OK"
    });

    setShowAssignmentModal(false);
    setSelectedAssignment("");
  } catch {
    swal({
      title: "Error!",
      text: "Failed to attach assignment",
      icon: "error",
      button: "OK"
    });
  }
};

  const toggleBatch = (batch) => {
    setBatchesSelected(prev =>
      prev.includes(batch)
        ? prev.filter(b => b !== batch)
        : [...prev, batch]
    );
  };

  if (loading) return <div className="page-loader">Loading MCQs...</div>;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="mcq-page">
      <h1 className="mcq-title">{category} MCQs</h1>

      <div className="mcq-grid">
        {mcqs.map(mcq => (
          <div key={mcq._id} className="mcq-card test-card">
            <h2>{mcq.topic}</h2>
            <p>📂 {mcq.category}</p>

            <p>❓ {mcq.questions.length} Questions</p>

            <div className="mcq-card-actions">
              <button
                onClick={() => {
                  setViewMcq(mcq);
                  setShowViewModal(true);
                }}
              >
                View
              </button>

              <button
                onClick={() => {
                  setSelectedMcq(mcq);
                  setShowAssignModal(true);
                }}
              >
                Assign
              </button>

              <button onClick={() => fetchAssignments(mcq)}>
                Attach Assignment
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ---------- ASSIGN MODAL ---------- */}
      {showAssignModal && (
        <div className="assign-modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="assign-modal" onClick={e => e.stopPropagation()}>
            <h2>Assign MCQ</h2>

            <select value={campus} onChange={e => setCampus(e.target.value)}>
              <option value="">Select College</option>
              {campuses.map(c => (
                <option key={c._id} value={c.name || c.Campusname}>
                  {c.name || c.college}
                </option>
              ))}
            </select>

            <select value={year} onChange={e => setYear(e.target.value)}>
              <option value="">Select Year</option>
              {years.map(y => (
                <option key={y._id} value={y.year || y.Year}>
                  {y.year || y.Year}
                </option>
              ))}
            </select>

            {batches.map(b => {
              const name = b.batch || b.Batchname;
              return (
                <label key={b._id}>
                  <input
                    type="checkbox"
                    checked={batchesSelected.includes(name)}
                    onChange={() => toggleBatch(name)}
                  />
                  {name}
                </label>
              );
            })}

            <button onClick={handleAssignMcq}>Assign</button>
            <button onClick={() => setShowAssignModal(false)}>Cancel</button>

            <p>{assignMessage}</p>
          </div>
        </div>
      )}

      {/* ---------- VIEW MODAL ---------- */}
      {/* ================= VIEW MCQ MODAL ================= */}
{showViewModal && viewMcq && (
  <div
    className="assign-modal-overlay"
    onClick={() => setShowViewModal(false)}
  >
    <div
      className="assign-modal view-mcq-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <h2>{viewMcq.topic} – Questions</h2>

      <div className="view-mcq-body">
        {viewMcq.questions.map((q, index) => (
          <div key={q._id} className="mcq-question-card">
            <h4>
              Q{index + 1}. {q.question}
            </h4>

            <ul className="mcq-options">
              <li>A. {q.optionA}</li>
              <li>B. {q.optionB}</li>
              <li>C. {q.optionC}</li>
              <li>D. {q.optionD}</li>
            </ul>

            <p className="mcq-answer">
              ✅ Correct Answer: {q.correctOption}
            </p>
          </div>
        ))}
      </div>

      <div className="modal-actions">
        <button
          className="btn-cancel"
          onClick={() => setShowViewModal(false)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

      {/* ---------- ASSIGNMENT MODAL ---------- */}
    {showAssignmentModal && (
  <div
    className="assign-modal-overlay"
    onClick={() => setShowAssignmentModal(false)}
  >
    <div
      className="assign-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="modal-title"> Attach Assignment</h2>

      {assignmentLoading ? (
        <div className="loader">Loading assignments...</div>
      ) : (
        <div className="dropdown-wrapper">
          <label>Select Assignment</label>
          <select
            value={selectedAssignment}
            onChange={(e) => setSelectedAssignment(e.target.value)}
          >
            <option value="">-- Choose Assignment --</option>
            {assignments.map((a) => (
              <option key={a._id} value={a._id}>
                {a.title || a.name || a._id}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="modal-actions">
        <button
          className="btn attach"
          disabled={!selectedAssignment}
          onClick={handleAttachAssignment}
        >
           Attach
        </button>

        <button
          className="btn cancel"
          onClick={() => setShowAssignmentModal(false)}
        >
           Cancel
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}